/**
 * User adoption-funnel taxonomy (#148, NFR-6.6).
 *
 * ONE source of truth for the funnel-stage names, shared by both emit paths so the
 * client RUM custom events and the server-authoritative OTLP counter + structured log
 * lines can never drift (AGENT_GUIDE rule 2). Like {@link ./data-rights} and
 * {@link ./legal}, this is a **control-plane / telemetry** concern — it never touches the
 * game WS wire, so it does NOT affect {@link PROTOCOL_VERSION}. (The RUM custom-event
 * shape is client-internal; the server counter/log line is observability, not protocol.)
 *
 * Two complementary emit paths, one funnel (#148 D148.1):
 *   - **Client** stages → CloudWatch RUM custom events (they share the RUM session with
 *     the auto-captured `errors`/`http` telemetry, so a drop-off can be correlated with
 *     the error a user hit on the way out).
 *   - **Server-authoritative** conversions → an OTLP `funnel_stage_total{stage}` counter
 *     (exact, independent of client sampling / ad-blockers) + a per-user structured log
 *     line carrying the pseudonymous identity keys (never a metric dimension — #148 D148.3).
 *
 * Identity is opaque + pseudonymous, never PII (#148 D148.4/D148.9): an anonymous
 * first-party `localStorage` `fkey` (funnel key) at the head, then the guest `playerId`,
 * then the registered `playerId`. Funnel telemetry is retention-aged, not point-deleted
 * (#148 D148.5) — see `docs/DATA_INVENTORY.md`.
 */

/**
 * The ordered adoption-funnel stages, head → conversion. Emitted from either path; the
 * `stage` string is the low-cardinality metric dimension on the server counter and the
 * `stage` field on the client RUM event / server log line.
 *
 * - `landed` — anonymous first app load (client, per session).
 * - `guest_created` — a persistent guest identity was minted (server, `POST /api/auth/guest`).
 * - `signup_started` — the sign-up form was opened/submitted (client).
 * - `account_registered` — a Cognito account was provisioned (server, `POST /api/account/register`).
 * - `account_linked` — a guest was upgraded to a registered account (server,
 *   `POST /api/account/claim`); its log line carries BOTH the old guest and new
 *   registered `playerId` so the pre-/post-signup journey stitches into one funnel.
 * - `checkout_started` — a Stripe Checkout Session was created (server, `POST /api/billing/checkout`).
 * - `purchase_granted` — credits were actually granted on a completed pay (server,
 *   webhook `checkout.session.completed`).
 * - `game_created` — the user created their first/next game (server, `POST /api/games`).
 *
 * Zero-commitment demo (#246, FR-17 · D246.9). The demo is the new top-of-funnel entry
 * for cold traffic; these stages are emitted as a seam for #148 to consume (this feature
 * emits, it does not build the funnel analytics):
 * - `demo_started` — a demo game was created (server, `POST /api/demo/games`).
 * - `demo_board_shown` — the demo board materialized in the visitor's browser (client).
 * - `demo_first_buzz` — the visitor buzzed in on their first demo clue (client).
 * - `demo_completed` — the demo reached its box (server, on a demo `game_over`).
 * - `demo_convert_clicked` — the visitor clicked a convert CTA on the end screen (client).
 *
 * SEO trivia pages (#295, FR-15.6). The organic-search entry surface: a visitor lands on a
 * crawlable `/trivia/<slug>` page and can launch the full pre-generated board as a game.
 * These reuse the same client RUM seam (slug carried as an event prop, never a metric
 * dimension — cardinality):
 * - `trivia_page_view` — a `/trivia/<slug>` page was viewed (client, with `slug` prop).
 * - `trivia_play_clicked` — the visitor clicked "Play this game" on a trivia page (client).
 * The launch itself continues into the existing `demo_started`/`game_created` conversions.
 *
 * The two canonical drop-offs the issue names are derived, not separately instrumented:
 * started-payment-not-completed = `checkout_started` − `purchase_granted`;
 * purchased-but-didn't-host = `purchase_granted` − `game_created`.
 */
export const FUNNEL_STAGES = [
  'landed',
  'trivia_page_view',
  'trivia_play_clicked',
  'demo_started',
  'demo_board_shown',
  'demo_first_buzz',
  'demo_completed',
  'demo_convert_clicked',
  'guest_created',
  'signup_started',
  'account_registered',
  'account_linked',
  'checkout_started',
  'purchase_granted',
  'game_created',
] as const;

/** A single adoption-funnel stage name (#148, NFR-6.6). */
export type FunnelStage = (typeof FUNNEL_STAGES)[number];

/** Type guard: is `value` a known funnel stage? */
export function isFunnelStage(value: unknown): value is FunnelStage {
  return (
    typeof value === 'string' && (FUNNEL_STAGES as readonly string[]).includes(value)
  );
}

/**
 * The RUM custom-event type under which client funnel stages are recorded. One event
 * type, discriminated by the `stage` field, so RUM console event views + Logs-Insights
 * queries filter on a single `eventType` (#148 §3.4).
 */
export const FUNNEL_RUM_EVENT_TYPE = 'funnel';

/**
 * The pseudonymous identity keys attached to a funnel event (#148 D148.4). All are
 * opaque, never PII: `fkey` is the first-party anonymous `localStorage` id (funnel head),
 * `playerId` is the guest-or-registered id, and `guestPlayerId` is the prior guest id on
 * an `account_linked` event (so both sides of the guest→registered claim are joinable).
 */
export interface FunnelIdentity {
  /** Anonymous first-party `localStorage` funnel key (head of funnel). */
  fkey?: string;
  /** The guest-or-registered player id at this stage. */
  playerId?: string;
  /** On `account_linked` only: the prior guest player id, so both keys join. */
  guestPlayerId?: string;
}

/**
 * The shape both emit paths build. Pure data — no clock/IO — so the client RUM event and
 * the server log line are byte-for-byte consistent in structure (only the transport
 * differs). `ts` is the caller-supplied wall-clock (epoch ms), injected (never read from
 * a clock here) so it stays pure and testable.
 */
export interface FunnelEvent extends FunnelIdentity {
  stage: FunnelStage;
  /** Wall-clock (epoch ms) the stage occurred, injected by the caller. */
  ts: number;
  /** Optional extra low-cardinality context (e.g. `sku` on checkout). Never PII. */
  props?: Record<string, string | number | boolean>;
}

/**
 * Build a {@link FunnelEvent} from a stage + identity + injected clock. Drops any
 * undefined identity key so the emitted object is minimal (no `fkey: undefined` noise in
 * RUM/logs). Pure — the single builder both paths call.
 */
export function buildFunnelEvent(
  stage: FunnelStage,
  ts: number,
  identity: FunnelIdentity = {},
  props?: Record<string, string | number | boolean>,
): FunnelEvent {
  return {
    stage,
    ts,
    ...(identity.fkey ? { fkey: identity.fkey } : {}),
    ...(identity.playerId ? { playerId: identity.playerId } : {}),
    ...(identity.guestPlayerId ? { guestPlayerId: identity.guestPlayerId } : {}),
    ...(props && Object.keys(props).length > 0 ? { props } : {}),
  };
}
